/**
 * SystemSheetSections
 *
 * Draws whatever the active game system says belongs on the character sheet
 * beyond the d20 block. The component knows six field kinds and nothing about
 * any particular system: Doomsworn's Doom Clock, Debt, Liens, Callings, rune
 * Grip and Wear all arrive as `SheetSection[]` from its adapter, and 5e — which
 * declares no sections — renders nothing at all.
 *
 * The rule this enforces is the one ISS-0132 is about: the engine owns these
 * numbers. Everything here is read-only. A player can see what a Draw cost
 * them; nobody, narrator included, edits it from the sheet.
 */

import type { Character } from '@machina/core';
import { sectionHasContent, type SheetField, type SheetSection, type SheetTone } from '@machina/core';
import { systemSheetSections } from '../lib/system-surface';
import { Icon } from './Icon';

// ---------------------------------------------------------------------------
// TONE
// ---------------------------------------------------------------------------

interface ToneColors {
  text: string;
  fill: string;
  border: string;
}

function toneColors(tone: SheetTone = 'neutral'): ToneColors {
  switch (tone) {
    case 'danger':
      return { text: '#f87171', fill: 'rgba(248, 113, 113, 0.85)', border: 'rgba(248, 113, 113, 0.35)' };
    case 'warning':
      return { text: '#fbbf24', fill: 'rgba(251, 191, 36, 0.85)', border: 'rgba(251, 191, 36, 0.35)' };
    case 'arcane':
      return { text: '#c084fc', fill: 'rgba(192, 132, 252, 0.85)', border: 'rgba(192, 132, 252, 0.35)' };
    default:
      return { text: 'rgba(226, 220, 240, 0.9)', fill: 'rgba(226, 220, 240, 0.6)', border: 'rgba(100, 80, 120, 0.3)' };
  }
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 9,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};

const HINT_STYLE: React.CSSProperties = {
  fontSize: 10,
  lineHeight: 1.45,
  color: 'rgba(160, 150, 180, 0.75)',
  marginTop: 4,
};

// ---------------------------------------------------------------------------
// FIELD KINDS
// ---------------------------------------------------------------------------

function ClockField({ field }: { field: SheetField }) {
  const colors = toneColors(field.tone);
  // Segments count the span above the floor — see the note in MeterField.
  const min = field.min ?? 0;
  const max = field.max ?? 8;
  const segments = Math.max(1, max - min);
  const filled = Math.min(segments, Math.max(0, (field.value ?? min) - min));

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {Array.from({ length: segments }, (_, i) => (
        <div
          key={i}
          style={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: i < filled ? colors.fill : 'transparent',
            border: `1px solid ${i < filled ? colors.fill : colors.border}`,
          }}
        />
      ))}
      <span style={{ marginLeft: 6, fontSize: 11, fontFamily: 'var(--font-mono)', color: colors.text }}>
        {filled}/{max}
      </span>
    </div>
  );
}

function MeterField({ field }: { field: SheetField }) {
  const colors = toneColors(field.tone);
  // Measured from the track's own floor, not from zero: a 1-to-6 Ruin track is
  // empty at 1, and a bar that assumes zero draws it already filling.
  const min = field.min ?? 0;
  const max = Math.max(min + 1, field.max ?? min + 1);
  const value = Math.min(max, Math.max(min, field.value ?? min));
  const pct = ((value - min) / (max - min)) * 100;

  return (
    <div>
      <div
        style={{
          height: 8,
          borderRadius: 4,
          background: 'rgba(0,0,0,0.35)',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: colors.fill }} />
      </div>
      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: colors.text, marginTop: 3 }}>
        {value} / {max}
      </div>
    </div>
  );
}

function EntriesField({ field }: { field: SheetField }) {
  const colors = toneColors(field.tone);
  const entries = field.entries ?? [];
  if (!entries.length) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(entry => (
        <div
          key={entry.id}
          style={{
            padding: '6px 8px',
            background: 'rgba(0,0,0,0.25)',
            borderLeft: `2px solid ${colors.border}`,
            borderRadius: 2,
            opacity: entry.resolved ? 0.5 : 1,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: entry.resolved ? 'rgba(160, 150, 180, 0.8)' : colors.text,
              textDecoration: entry.resolved ? 'line-through' : 'none',
            }}
          >
            {entry.title}
          </div>
          {entry.detail && (
            <div style={{ fontSize: 10, color: 'rgba(160, 150, 180, 0.7)', marginTop: 2 }}>
              {entry.detail}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Field({ field }: { field: SheetField }) {
  const colors = toneColors(field.tone);

  const body = (() => {
    switch (field.kind) {
      case 'clock':
        return <ClockField field={field} />;
      case 'meter':
        return <MeterField field={field} />;
      case 'entries':
        return <EntriesField field={field} />;
      case 'tags':
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {(field.tags ?? []).map(tag => (
              <span
                key={tag}
                style={{
                  padding: '2px 8px',
                  borderRadius: 10,
                  fontSize: 10,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
        );
      case 'text':
        return (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              // A blank declaration is shown as absent rather than as an empty
              // box, so the prompt in the hint has something to point at.
              color: field.text ? colors.text : 'rgba(160, 150, 180, 0.5)',
              fontStyle: field.text ? 'normal' : 'italic',
            }}
          >
            {field.text || 'Not set'}
          </div>
        );
      case 'score':
      default:
        return (
          <div style={{ fontSize: 20, fontWeight: 300, fontFamily: 'var(--font-display)', color: colors.text }}>
            {field.value ?? 0}
          </div>
        );
    }
  })();

  // An `entries` field with nothing in it contributes no label either — an
  // empty "Liens" heading reads as a bug rather than as an absence.
  if (field.kind === 'entries' && !(field.entries ?? []).length) return null;

  return (
    <div>
      <div style={LABEL_STYLE}>{field.label}</div>
      {body}
      {field.hint && <div style={HINT_STYLE}>{field.hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTIONS
// ---------------------------------------------------------------------------

function SystemSection({ section, accentColor }: { section: SheetSection; accentColor: string }) {
  return (
    <div
      style={{
        marginBottom: 16,
        padding: 12,
        background: 'rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(100, 80, 120, 0.25)',
        borderRadius: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <Icon name="crystal" size={12} color={accentColor} />
        <span
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            color: accentColor,
            fontWeight: 600,
          }}
        >
          {section.title}
        </span>
      </div>
      {section.blurb && (
        <div style={{ fontSize: 10, color: 'rgba(160, 150, 180, 0.7)', marginBottom: 10, lineHeight: 1.45 }}>
          {section.blurb}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {section.fields.map(field => (
          <Field key={field.id} field={field} />
        ))}
      </div>
    </div>
  );
}

/**
 * All sections the active system contributes, or nothing at all under a system
 * that contributes none — which is every 5e campaign, unchanged.
 */
export function SystemSheetSections({
  character,
  accentColor,
}: {
  character: Character;
  accentColor: string;
}) {
  const sections = systemSheetSections(character).filter(sectionHasContent);
  if (!sections.length) return null;

  return (
    <>
      {sections.map(section => (
        <SystemSection key={section.id} section={section} accentColor={accentColor} />
      ))}
    </>
  );
}
